import { api } from './client';
import { formatChartDay } from '@/lib/datetime';

// Types matching the API
export interface OverviewStats {
  impressions: {
    value: number;
    change: string;
    trend: 'up' | 'down' | 'neutral';
  };
  clicks: {
    value: number;
    change: string;
    trend: 'up' | 'down' | 'neutral';
  };
  ctr: {
    value: string;
    change: string;
    trend: 'up' | 'down' | 'neutral';
  };
  completes: {
    value: number;
    change: string;
    trend: 'up' | 'down' | 'neutral';
  };
}

export interface PerformanceDataPoint {
  date: string;
  impressions: number;
  clicks: number;
  completes: number;
  requests: number;
}

export interface TopCampaign {
  id: string;
  name: string;
  impressions: number;
  clicks: number;
  ctr: string;
}

export interface TopCountry {
  code: string;
  name: string;
  impressions: number;
  percentage: number;
}

export interface AdTypeStats {
  ad_type: string;
  impressions: number;
  clicks: number;
  ctr: number;
}

export interface PlatformStats {
  platform: string;
  impressions: number;
  clicks: number;
  completes: number;
  ctr: number;
}

// Filter params
export interface StatsFilters {
  app_id?: string;
  campaign_id?: string;
  country?: string;
  platform?: string;
}

// API response types from backend
interface ApiOverviewStats {
  impressions: number;
  clicks: number;
  completes: number;
  requests: number;
  ctr: number;
  trend: {
    impressions: number;
    clicks: number;
    completes: number;
    requests: number;
  };
}

interface ApiCampaignStats {
  campaign_id: string;
  campaign_name: string;
  impressions: number;
  clicks: number;
  ctr: number;
}

interface ApiCountryStats {
  country: string;
  impressions: number;
  clicks: number;
  ctr: number;
}

// Country code to name mapping
const countryNames: Record<string, string> = {
  US: 'United States',
  GB: 'United Kingdom',
  DE: 'Germany',
  CA: 'Canada',
  AU: 'Australia',
  FR: 'France',
  JP: 'Japan',
  BR: 'Brazil',
  IN: 'India',
  CN: 'China',
  KR: 'South Korea',
  RU: 'Russia',
  MX: 'Mexico',
  ES: 'Spain',
  IT: 'Italy',
  NL: 'Netherlands',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  FI: 'Finland',
  PL: 'Poland',
  TR: 'Turkey',
  ID: 'Indonesia',
  TH: 'Thailand',
  VN: 'Vietnam',
  PH: 'Philippines',
  MY: 'Malaysia',
  SG: 'Singapore',
  Unknown: 'Unknown',
};

function formatTrend(value: number): { change: string; trend: 'up' | 'down' | 'neutral' } {
  if (value === 0) return { change: '0%', trend: 'neutral' };
  const sign = value > 0 ? '+' : '';
  return {
    change: `${sign}${value}%`,
    trend: value > 0 ? 'up' : 'down',
  };
}

// Helper to build date range query params
function buildDateQuery(days: number): string {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return `&start_date=${start.toISOString().slice(0, 10)}&end_date=${end.toISOString().slice(0, 10)}`;
}

// Helper to build query string from filters
function buildFilterQuery(filters?: StatsFilters): string {
  if (!filters) return '';
  const params: string[] = [];
  if (filters.app_id) params.push(`app_id=${encodeURIComponent(filters.app_id)}`);
  if (filters.campaign_id) params.push(`campaign_id=${encodeURIComponent(filters.campaign_id)}`);
  if (filters.country) params.push(`country=${encodeURIComponent(filters.country)}`);
  if (filters.platform) params.push(`platform=${encodeURIComponent(filters.platform)}`);
  return params.length > 0 ? '&' + params.join('&') : '';
}

export async function getOverviewStats(days?: number, filters?: StatsFilters): Promise<OverviewStats> {
  try {
    const filterQuery = buildFilterQuery(filters);
    const dateQuery = days ? buildDateQuery(days) : '';
    const data = await api.get<ApiOverviewStats>(`/admin/stats/overview?_=1${dateQuery}${filterQuery}`);

    const impressionsTrend = formatTrend(data.trend.impressions);
    const clicksTrend = formatTrend(data.trend.clicks);
    const completesTrend = formatTrend(data.trend.completes);

    // Calculate CTR trend
    const ctrValue = data.ctr;
    const prevImpressions = data.trend.impressions !== 0
      ? data.impressions / (1 + data.trend.impressions / 100)
      : data.impressions;
    const prevClicks = data.trend.clicks !== 0
      ? data.clicks / (1 + data.trend.clicks / 100)
      : data.clicks;
    const prevCtr = prevImpressions > 0 ? (prevClicks / prevImpressions) * 100 : 0;
    const ctrTrendPct = prevCtr > 0 ? Math.round((ctrValue - prevCtr) / prevCtr * 100) : 0;
    const ctrTrend = formatTrend(ctrTrendPct);

    return {
      impressions: {
        value: data.impressions,
        ...impressionsTrend,
      },
      clicks: {
        value: data.clicks,
        ...clicksTrend,
      },
      ctr: {
        value: `${ctrValue.toFixed(2)}%`,
        ...ctrTrend,
      },
      completes: {
        value: data.completes,
        ...completesTrend,
      },
    };
  } catch (error) {
    console.error('Failed to fetch overview stats:', error);
    // Return zeros on error
    return {
      impressions: { value: 0, change: '0%', trend: 'neutral' },
      clicks: { value: 0, change: '0%', trend: 'neutral' },
      ctr: { value: '0.00%', change: '0%', trend: 'neutral' },
      completes: { value: 0, change: '0%', trend: 'neutral' },
    };
  }
}

export async function getPerformanceData(days: number = 7, filters?: StatsFilters): Promise<PerformanceDataPoint[]> {
  try {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const startStr = startDate.toISOString().slice(0, 10);
    const endStr = endDate.toISOString().slice(0, 10);
    const filterQuery = buildFilterQuery(filters);

    const data = await api.get<PerformanceDataPoint[]>(
      `/admin/stats/performance?start_date=${startStr}&end_date=${endStr}${filterQuery}`
    );

    // Format dates for display
    return data.map((point: PerformanceDataPoint) => ({
      ...point,
      date: formatChartDay(point.date),
    }));
  } catch (error) {
    console.error('Failed to fetch performance data:', error);
    // Return empty array with zeros for the period
    const result: PerformanceDataPoint[] = [];
    const now = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      result.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        impressions: 0,
        clicks: 0,
        completes: 0,
        requests: 0,
      });
    }
    return result;
  }
}

export async function getTopCampaigns(days: number = 30, filters?: StatsFilters): Promise<TopCampaign[]> {
  try {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    const startStr = startDate.toISOString().slice(0, 10);
    const endStr = endDate.toISOString().slice(0, 10);
    const filterQuery = buildFilterQuery(filters);
    const data = await api.get<ApiCampaignStats[]>(`/admin/stats/by-campaign?start_date=${startStr}&end_date=${endStr}${filterQuery}`);

    return data
      .filter((c: ApiCampaignStats) => c.impressions > 0 || c.clicks > 0)
      .slice(0, 5)
      .map((c: ApiCampaignStats) => ({
        id: c.campaign_id,
        name: c.campaign_name,
        impressions: c.impressions,
        clicks: c.clicks,
        ctr: `${c.ctr.toFixed(2)}%`,
      }));
  } catch (error) {
    console.error('Failed to fetch top campaigns:', error);
    return [];
  }
}

export async function getTopCountries(days: number = 30, filters?: StatsFilters): Promise<TopCountry[]> {
  try {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    const startStr = startDate.toISOString().slice(0, 10);
    const endStr = endDate.toISOString().slice(0, 10);
    const filterQuery = buildFilterQuery(filters);
    const data = await api.get<ApiCountryStats[]>(`/admin/stats/by-country?start_date=${startStr}&end_date=${endStr}${filterQuery}`);

    const totalImpressions = data.reduce((sum: number, c: ApiCountryStats) => sum + c.impressions, 0);

    return data
      .filter((c: ApiCountryStats) => c.impressions > 0)
      .slice(0, 8)
      .map((c: ApiCountryStats) => ({
        code: c.country === 'Unknown' ? 'XX' : c.country,
        name: countryNames[c.country] || c.country,
        impressions: c.impressions,
        percentage: totalImpressions > 0
          ? Math.round((c.impressions / totalImpressions) * 1000) / 10
          : 0,
      }));
  } catch (error) {
    console.error('Failed to fetch top countries:', error);
    return [];
  }
}

export async function getAdTypeStats(days: number = 30, filters?: StatsFilters): Promise<AdTypeStats[]> {
  try {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    const startStr = startDate.toISOString().slice(0, 10);
    const endStr = endDate.toISOString().slice(0, 10);
    const filterQuery = buildFilterQuery(filters);
    const data = await api.get<AdTypeStats[]>(`/admin/stats/by-ad-type?start_date=${startStr}&end_date=${endStr}${filterQuery}`);
    return data;
  } catch (error) {
    console.error('Failed to fetch ad type stats:', error);
    return [];
  }
}

export async function getPlatformStats(days: number = 30, filters?: StatsFilters): Promise<PlatformStats[]> {
  try {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    const startStr = startDate.toISOString().slice(0, 10);
    const endStr = endDate.toISOString().slice(0, 10);
    const filterQuery = buildFilterQuery(filters);
    const data = await api.get<PlatformStats[]>(`/admin/stats/by-platform?start_date=${startStr}&end_date=${endStr}${filterQuery}`);
    return data;
  } catch (error) {
    console.error('Failed to fetch platform stats:', error);
    return [];
  }
}

// Flow comparison: force fullscreen webview vs ad-click via stub game.
export interface FlowBucket {
  clicks: number;
  regs: number;
  deps: number;
  revenue_cents: number;
  cr_reg: number;   // % regs / clicks
  cr_dep: number;   // % deps / clicks
  epc: number;      // revenue_cents/100 / clicks
}

export interface FlowCompareRow {
  app_id: string;
  app_name: string;
  force: FlowBucket;
  ad_click: FlowBucket;
}

export interface FlowCompareData {
  start: string;
  end: string;
  rows: FlowCompareRow[];
  totals: { force: FlowBucket; ad_click: FlowBucket };
}

export async function getFlowCompare(days: number = 30): Promise<FlowCompareData | null> {
  try {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    const startStr = startDate.toISOString().slice(0, 10);
    const endStr = endDate.toISOString().slice(0, 10);
    return await api.get<FlowCompareData>(
      `/admin/stats/flow-compare?start_date=${startStr}&end_date=${endStr}`
    );
  } catch (error) {
    console.error('Failed to fetch flow-compare stats:', error);
    return null;
  }
}

// Format large numbers with commas
export function formatNumber(num: number): string {
  return num.toLocaleString('en-US');
}

// Per-country conversion (clicks + reg/dep/revenue) for the offers CSV export.
export interface CountryConversion {
  country: string;
  clicks: number;
  regs: number;
  deps: number;
  revenue_cents: number;
}

export async function getConversionByCountry(
  range: number | { start: string; end: string } = 30,
): Promise<CountryConversion[]> {
  let startStr: string;
  let endStr: string;
  if (typeof range === 'number') {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - range);
    startStr = startDate.toISOString().slice(0, 10);
    endStr = endDate.toISOString().slice(0, 10);
  } else {
    startStr = range.start;
    endStr = range.end;
  }
  return api.get<CountryConversion[]>(
    `/admin/stats/conversion-by-country?start_date=${startStr}&end_date=${endStr}`
  );
}
